import React, { useEffect, useState } from "react";
import { enqueueSnackbar } from "notistack";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../ui/card";
import { Label } from "../../ui/label";
import { Button } from "../../ui/button";
import { Badge } from "../../ui/badge";
import { UserCheck, Loader2, X, ChevronRight, ChevronLeft, Shield, Check, User, Search } from "lucide-react";
import { api } from "../../../services/api";

interface Perfil {
  id: string;
  perfil: string;
}

interface Militar {
  id: number;
  nick: string;
}

interface AtribuirPerfisModalProps {
  isOpen: boolean;
  onClose: () => void;
  militar: Militar | null;
  onSuccess?: () => void;
}

export function AtribuirPerfisModal({
  isOpen,
  onClose,
  militar,
  onSuccess
}: AtribuirPerfisModalProps) {
  const [loading, setLoading] = useState(false);
  const [loadingPerfis, setLoadingPerfis] = useState(false);
  const [erroCarregarPerfis, setErroCarregarPerfis] = useState(false);
  const [todosPerfis, setTodosPerfis] = useState<Perfil[]>([]);
  const [perfisDoMilitar, setPerfisDoMilitar] = useState<string[]>([]);
  const [filterText, setFilterText] = useState('');

  useEffect(() => {
    if (isOpen && militar) {
      setErroCarregarPerfis(false);
      carregarPerfis();
      carregarPerfisDoMilitar();
    } else {
      // Reset ao fechar
      setPerfisDoMilitar([]);
      setErroCarregarPerfis(false);
    }
  }, [isOpen, militar]);

  const carregarPerfis = async () => {
    try {
      setLoadingPerfis(true);
      const response = await api.get("/perfis");
      setTodosPerfis(response.data);
    } catch (error) {
      console.error("Erro ao carregar perfis:", error);
      enqueueSnackbar("Erro ao carregar perfis", { variant: "error" });
      // Fallback para dados mock
      setTodosPerfis([
        { id: "1", perfil: "Administrador" },
        { id: "2", perfil: "Oficial" },
        { id: "3", perfil: "Praça" },
        { id: "4", perfil: "Instrutor" },
      ]);
    } finally {
      setLoadingPerfis(false);
    }
  };

  const carregarPerfisDoMilitar = async () => {
    if (!militar) return;

    try {
      const response = await api.get(`/perfisMilitares/${militar.id}`);
      // Extrair apenas os nomes dos perfis
      const perfisNomes = response.data.map((pm: any) => pm.perfil?.perfil || pm.perfil);
      setPerfisDoMilitar(perfisNomes);
    } catch (error) {
      console.error("Erro ao carregar perfis do militar:", error);
      setErroCarregarPerfis(true);
      enqueueSnackbar("Erro ao carregar perfis do militar. Salvar está bloqueado para evitar perda de dados.", { variant: "error" });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!militar) return;

    if (erroCarregarPerfis) {
      enqueueSnackbar("Não é possível salvar pois os perfis atuais não foram carregados. Feche e abra o modal novamente.", { variant: "error" });
      return;
    }

    try {
      setLoading(true);

      // Atualizar perfis do militar
      await api.put(`/perfisMilitares/${militar.id}`, {
        perfis: perfisDoMilitar
      });

      enqueueSnackbar("Perfis atualizados com sucesso!", { variant: "success" });

      if (onSuccess) {
        onSuccess();
      }

      handleClose();
    } catch (error: any) {
      enqueueSnackbar(error.response?.data?.message || "Erro ao atualizar perfis", {
        variant: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setPerfisDoMilitar([]);
    setFilterText('');
    onClose();
  };

  const adicionarPerfil = (nomePerfil: string) => {
    setPerfisDoMilitar(prev => [...prev, nomePerfil]);
  };

  const removerPerfil = (nomePerfil: string) => {
    setPerfisDoMilitar(prev => prev.filter(p => p !== nomePerfil));
  };

  const perfisDisponiveis = todosPerfis.filter(
    p => !perfisDoMilitar.includes(p.perfil)
  );

  const perfisSelecionados = todosPerfis.filter(
    p => perfisDoMilitar.includes(p.perfil)
  );

  const perfisDisponiveisFiltrados = filterText
    ? perfisDisponiveis.filter(p => p.perfil.toLowerCase().includes(filterText.toLowerCase()))
    : perfisDisponiveis;

  const perfisSelecionadosFiltrados = filterText
    ? perfisSelecionados.filter(p => p.perfil.toLowerCase().includes(filterText.toLowerCase()))
    : perfisSelecionados;

  if (!isOpen || !militar) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <Card className="w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
          <div>
            <CardTitle className="flex items-center gap-2">
              <UserCheck className="h-5 w-5 text-[oklch(0.45_0.15_142)]" />
              Gerenciar Perfis
            </CardTitle>
            <CardDescription>
              Atribuir perfis ao militar: <strong>{militar.nick}</strong>
            </CardDescription>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleClose}
            className="h-6 w-6 p-0"
            disabled={loading}
          >
            <X className="h-4 w-4" />
          </Button>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Informações do Militar */}
            <div className="flex items-center gap-3 p-4 bg-muted/50 rounded-lg border border-border">
              <div className="w-12 h-12 bg-[oklch(0.45_0.15_142)]/10 rounded-lg flex items-center justify-center overflow-hidden">
                <img
                  src={`http://www.habbo.com.br/habbo-imaging/avatarimage?&user=${militar.nick}&action=std&direction=5&head_direction=2&img_format=png&gesture=std&headonly=1&size=l`}
                  alt={`Avatar de ${militar.nick}`}
                  className="max-w-full max-h-full object-contain"
                  onError={(e) => {
                    e.currentTarget.style.display = 'none';
                    e.currentTarget.nextElementSibling?.classList.remove('hidden');
                  }}
                />
                <User className="w-5 h-5 text-[oklch(0.45_0.15_142)] hidden" />
              </div>
              <div>
                <h3 className="font-semibold text-foreground">{militar.nick}</h3>
                <p className="text-sm text-muted-foreground">
                  {perfisSelecionados.length} perfil(s) atribuído(s)
                </p>
              </div>
            </div>

            {/* Gerenciamento de Perfis */}
            <div className="space-y-3">
              <Label className="flex items-center gap-2">
                <Shield className="w-4 h-4" />
                Perfis do Militar
              </Label>
              <p className="text-xs text-muted-foreground">
                Clique nos perfis disponíveis para atribuir ou nos perfis atribuídos para remover
              </p>

              {loadingPerfis ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-[oklch(0.45_0.15_142)]" />
                  <span className="ml-2 text-sm text-muted-foreground">Carregando perfis...</span>
                </div>
              ) : (
                <>
                  {/* Filtro de permissão */}
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <input
                      type="text"
                      value={filterText}
                      onChange={(e) => setFilterText(e.target.value)}
                      placeholder="Filtrar permissão..."
                      className="w-full pl-9 pr-3 py-2 text-sm border border-border rounded-md bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    {filterText && (
                      <button
                        type="button"
                        onClick={() => setFilterText('')}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                      >
                        <X className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    {/* Perfis Disponíveis */}
                    <div className="space-y-2">
                      <h4 className="text-sm font-medium text-foreground flex items-center gap-1">
                        Disponíveis ({perfisDisponiveisFiltrados.length}{filterText ? `/${perfisDisponiveis.length}` : ''})
                      </h4>
                      <div className="border border-border rounded-lg p-3 h-64 overflow-y-auto bg-muted/30">
                        {perfisDisponiveisFiltrados.length === 0 ? (
                          <p className="text-xs text-muted-foreground text-center py-6">
                            {filterText ? 'Nenhum resultado encontrado' : 'Todos os perfis foram atribuídos'}
                          </p>
                        ) : (
                          <div className="space-y-2">
                            {perfisDisponiveisFiltrados.map((perfil) => (
                              <div
                                key={perfil.id}
                                className="flex items-center justify-between p-3 bg-background border border-border rounded hover:bg-muted hover:border-[oklch(0.45_0.15_142)] cursor-pointer transition-all"
                                onClick={() => adicionarPerfil(perfil.perfil)}
                              >
                                <span className="text-sm font-medium text-foreground">
                                  {perfil.perfil}
                                </span>
                                <ChevronRight className="w-4 h-4 text-muted-foreground" />
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Perfis Atribuídos */}
                    <div className="space-y-2">
                      <h4 className="text-sm font-medium text-foreground flex items-center gap-1">
                        Atribuídos ({perfisSelecionadosFiltrados.length}{filterText ? `/${perfisSelecionados.length}` : ''})
                      </h4>
                      <div className="border border-green-200 dark:border-green-800 rounded-lg p-3 h-64 overflow-y-auto bg-green-50 dark:bg-green-900/20">
                        {perfisSelecionadosFiltrados.length === 0 ? (
                          <p className="text-xs text-muted-foreground text-center py-6">
                            {filterText ? 'Nenhum resultado encontrado' : 'Nenhum perfil atribuído'}
                          </p>
                        ) : (
                          <div className="space-y-2">
                            {perfisSelecionadosFiltrados.map((perfil) => (
                              <div
                                key={perfil.id}
                                className="flex items-center justify-between p-3 bg-green-100 dark:bg-green-800/30 rounded border border-green-200 dark:border-green-700 hover:bg-green-200 dark:hover:bg-green-700/40 cursor-pointer transition-all"
                                onClick={() => removerPerfil(perfil.perfil)}
                              >
                                <ChevronLeft className="w-4 h-4 text-green-600 dark:text-green-400" />
                                <span className="text-sm font-medium text-green-800 dark:text-green-200">
                                  {perfil.perfil}
                                </span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </>
              )}

              {/* Resumo dos Perfis */}
              {perfisSelecionados.length > 0 && (
                <div className="mt-3">
                  <p className="text-xs text-muted-foreground mb-2">Perfis atribuídos:</p>
                  <div className="flex flex-wrap gap-2">
                    {perfisSelecionados.map((perfil) => (
                      <Badge
                        key={perfil.id}
                        className="bg-[oklch(0.45_0.15_142)]/10 text-[oklch(0.45_0.15_142)] border-[oklch(0.45_0.15_142)]/20"
                      >
                        <Check className="w-3 h-3 mr-1" />
                        {perfil.perfil}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="flex gap-2 pt-4 border-t">
              <Button
                type="button"
                variant="outline"
                onClick={handleClose}
                className="flex-1"
                disabled={loading}
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                disabled={loading || loadingPerfis || erroCarregarPerfis}
                title={erroCarregarPerfis ? "Salvar bloqueado: erro ao carregar perfis atuais" : undefined}
                className="flex-1 bg-[oklch(0.45_0.15_142)] hover:bg-[oklch(0.40_0.15_142)] text-white disabled:opacity-50"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Salvando...
                  </>
                ) : (
                  <>
                    <Check className="w-4 h-4 mr-2" />
                    Salvar Perfis
                  </>
                )}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
